import { Button, TextBalancer } from 'components/common';
import { AddToCart, CartLineQuantityInput } from 'lib/cart';
import { isCustomizableMeal, Product } from 'lib/product';
import {
  getNextVisibleSelectedAddons,
  ProductVariantAddonSelectGroup,
  shallowEqual,
  useProductVariantAddonOptions,
  useProductVariantAddonRules,
} from 'lib/productAddon';
import ProductPrice from 'lib/sellingProduct/ProductPrice';
import useProductCustomization from 'lib/sellingProduct/useProductCustomization';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatMarkdownToHtml,
  isEdgeBrowser,
  isIEBrowser,
} from 'utils';
import {
  ProductVariantPicker,
  utils as productVariantsUtils,
} from 'lib/productVariants';
import ProductShare from 'lib/sellingProduct/ProductShare';

const ProductDetail = ({
  product,
  onAddToCart,
  onFirstTimeAdded,
  isQuickView,
}: {
  product: Product;
  onAddToCart?: () => void;
  onFirstTimeAdded?: () => void;
  isQuickView?: boolean;
}) => {
  const {
    selectedAddons,
    selectedVariant,
    selectedAddonOptionValueIds,
    quantity,
    setQuantity,
    setSelectedAddons,
    setSelectedVariant,
    isAnyAddonOutOfStock,
  } = useProductCustomization();

  const [isAddonOptionsValid, setIsAddonOptionsValid] = useState(
    true,
  );

  const {
    addonRules,
    isLoading: isAddonRulesLoading,
  } = useProductVariantAddonRules(selectedVariant?.id);

  const {
    selectAddonOptionValueById,
    addonOptions,
    selectIngredientById,
    isLoading: isAddonOptionsLoading,
  } = useProductVariantAddonOptions(selectedVariant?.id);

  const selectedAddonOptionValues = useMemo(
    () =>
      selectedAddonOptionValueIds
        .map((selectedAddonOptionValueId) =>
          selectAddonOptionValueById(selectedAddonOptionValueId),
        )
        .filter(Boolean),
    [selectAddonOptionValueById, selectedAddonOptionValueIds],
  );

  const resetMeal = useCallback(() => setSelectedAddons({}), [
    setSelectedAddons,
  ]);

  useEffect(() => {
    // @ts-ignore
    const nextVisibleSelectedAddons = getNextVisibleSelectedAddons({
      addonOptions: addonOptions ?? [],
      addonRules,
      selectedAddons,
    });

    setIsAddonOptionsValid(
      shallowEqual(selectedAddons, nextVisibleSelectedAddons),
    );
  }, [addonOptions, addonRules, selectedAddons]);

  const isCustomizableProduct = useMemo(
    () => isCustomizableMeal(product),
    [product],
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-col">
        <TextBalancer
          as="h3"
          className="tw-tracking-tight tw-font-extrabold tw-text-[#333] tw-leading-tight tw-text-4xl tw-mb-1"
        >
          {product.name}
        </TextBalancer>
        <ProductPrice
          // @ts-ignore
          variant={selectedVariant}
          addonOptionValues={selectedAddonOptionValues}
          className="tw-font-bold tw-mb-0 tw-text-3xl"
        />
      </div>
      <p
        dangerouslySetInnerHTML={{
          __html: formatMarkdownToHtml(product.short_description),
        }}
        style={{
          lineHeight: 1.4,
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          msWordBreak: 'break-all',
          wordBreak: 'break-word',
          msHyphens: 'auto',
          MozHyphens: 'auto',
          WebkitHyphens: 'auto',
          hyphens: 'auto',
          whiteSpace: 'pre-line',
          marginBottom: 0,
        }}
      />
      {productVariantsUtils.hasProductVariants(product) && (
        <ProductVariantPicker
          // @ts-ignore
          productVariants={product.variants}
          // @ts-ignore
          productOptions={product.options}
          // @ts-ignore
          value={{ variant: selectedVariant }}
          onChange={({ variant }) => {
            setSelectedVariant(variant);
          }}
        />
      )}
      {isCustomizableProduct && (
        <ProductVariantAddonSelectGroup
          // @ts-ignore
          productVariantId={selectedVariant?.id}
          // @ts-ignore
          value={selectedAddons}
          onChange={setSelectedAddons}
          className="tw-space-y-4 addons-select-dialog"
          showCoachmark={false}
          isQuickView={isQuickView}
        />
      )}
      <div className="tw-flex tw-items-center tw-mt-0 tw-mb-4 tw-w-full">
        <div className="details-filter-row details-row-size tw-mb-0">
          <label htmlFor="qty" className="product-dialog-qty">
            Qty:
          </label>
          <div className="tw-flex tw-items-center tw-gap-4 tw-flex-[1_0_220px]">
            <div className="product-details-quantity tw-max-w-[unset]">
              <CartLineQuantityInput
                id="qty"
                name="quantity"
                // @ts-ignore
                productVariant={selectedVariant}
                value={quantity}
                onChange={setQuantity}
                data-decimals="0"
                required
              />
            </div>

            {isCustomizableProduct && (
              <div
                className="tw-text-[#666] tw-cursor-pointer tw-flex tw-items-center tw-gap-2 hover:tw-text-[#A42E27] tw-transition-colors tw-duration-200 tw-leading-none"
                onClick={resetMeal}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                  <path d="M21 3v5h-5" />
                </svg>
                Reset My Meal
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="product-details-action tw-flex-row tw-flex-nowrap tw-gap-4 tw-mb-4 tw-justify-start">
        <AddToCart
          // @ts-ignore
          product={product}
          // @ts-ignore
          productVariant={selectedVariant}
          quantity={quantity}
          // @ts-ignore
          addonOptionValueIds={selectedAddonOptionValueIds}
          onFirstItemAdded={onFirstTimeAdded}
        >
          {({
            loading,
            isOutOfStock,
            canAddToCart,
            addToCart,
            isAddingToCart,
          }) => {
            const getLabel = () => {
              if (
                loading ||
                isAddonOptionsLoading ||
                isAddonRulesLoading
              )
                return 'Loading...';
              if (!isAddonOptionsValid) return 'Plz reset meal';
              if (isOutOfStock || isAnyAddonOutOfStock)
                return 'We Sold Out!';
              return 'ADD TO CART';
            };

            const disabled =
              !canAddToCart ||
              isAnyAddonOutOfStock ||
              !isAddonOptionsValid ||
              isAddonOptionsLoading ||
              isAddonRulesLoading;

            const handleAddToCart = async () => {
              try {
                await addToCart();
                onAddToCart?.();
              } catch (err) {}
            };

            return (
              // @ts-ignore
              <Button
                style={
                  isIEBrowser() || isEdgeBrowser()
                    ? { minHeight: '4rem' }
                    : {}
                }
                disabled={disabled}
                variant="outlined"
                color={'primary'}
                loading={isAddingToCart}
                // @ts-ignore
                onClick={handleAddToCart}
                className={`btn-product btn-cart tw-min-w-0 tw-px-4 md:tw-px-6 ${
                  !loading && !isAddonOptionsValid
                    ? 'invalid-addon'
                    : ''
                }`}
              >
                {!isAddonOptionsValid && !loading ? (
                  <em className="fa fa-exclamation-triangle tw-text-lg tw-mr-2" />
                ) : null}
                <span>{getLabel()}</span>
              </Button>
            );
          }}
        </AddToCart>
      </div>

      <ProductShare
        product={product}
        selectedVariant={selectedVariant ?? undefined}
        selectedAddonOptionValues={selectedAddonOptionValues}
        selectIngredientById={selectIngredientById}
        isDialog
        selectedAddons={selectedAddons}
      />

      {(product?.tags?.length ?? 0) > 0 && (
        <div className="product-details-footer">
          <div className="product-cat tw-mb-0">
            {product?.tags?.map((tag, index) => (
              <span key={tag.id} className="mr-0">
                {tag.name}
                {index < (product.tags?.length ?? 0) - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
